import React, { useState, useEffect, useMemo } from 'react';
import { baseUrl } from '../components/BaseUrl';
import { Link } from 'react-router-dom';
import TopBar  from '../components/Topbar';
import { MyProSidebarProvider } from "../components/SidebarContext";
import '../App.css';
import axios from 'axios';
import Swal from "sweetalert2";
import AOS from 'aos';
import 'aos/dist/aos.css';
AOS.init();

const CourseCatalog = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all', 'available', 'premium', 'free'
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    const token = localStorage.getItem("codefussion-login-token");
    setLoading(true);
    axios.get(`${baseUrl}get_courses.php`, {
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: token
      }
    })
    .then(response => {
      setData(response.data);
      setLoading(false);
    })
    .catch((error) => {
      Swal.fire({
        title: "Error",
        text: "An error occurred. Please try again later",
        icon: "error",
        confirmButtonText: "OK",
        backdrop: `rgba(143, 70, 0, 0.3) left top no-repeat`
      });
      setLoading(false);
    });
  }

  // Real-time filtering using useMemo for performance
  const filteredCourses = useMemo(() => {
    let filtered = data;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(course => 
        course.course_title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        course.course_description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Category filter
    switch(filter) {
      case 'available':
        return filtered.filter(course => course.available === "true" || course.available === true);
      case 'premium':
        return filtered.filter(course => 
          (course.available === "true" || course.available === true) && 
          (course.subscription_required === "true" || course.subscription_required === true)
        );
      case 'free':
        return filtered.filter(course => 
          (course.available === "true" || course.available === true) && 
          !(course.subscription_required === "true" || course.subscription_required === true)
        );
      default:
        return filtered;
    }
  }, [data, filter, searchTerm]);

  const showCourses = () => {
    try {
      if (filteredCourses.length === 0) {
        return (
          <div className="col-12 text-center py-5">
            <div className="no-courses">
              <i className="fas fa-search fa-3x text-muted mb-3"></i>
              <h4>No courses found</h4>
              <p className="text-muted">
                {searchTerm 
                  ? `No courses match "${searchTerm}". Try a different search term.`
                  : "No courses match your selected filters. Try changing your filter options."}
              </p>
              {(searchTerm || filter !== 'all') && (
                <button 
                  className="btn btn-primary mt-2"
                  onClick={() => {
                    setSearchTerm('');
                    setFilter('all');
                  }}
                >
                  Clear Filters
                </button>
              )}
            </div>
          </div>
        );
      }

      return filteredCourses.map((item, index) => {
        const isAvailable = item.available === "true" || item.available === true;
        const isPremium = item.subscription_required === "true" || item.subscription_required === true;
        let shortenString = item.course_description?.substring(0, 36).concat('...') || 'No description available';
        
        return(
          <div data-aos="fade-up" data-aos-duration="1000" className="col-lg-3 col-md-6 col-sm-6 item" key={item.id}>
            <div className={`card card-body mb-4 card-block course-card ${!isAvailable ? 'course-unavailable' : ''}`}>
              
              {/* Availability Badge */}
              {!isAvailable && (
                <div className="unavailable-badge">
                  COMING SOON
                </div>
              )}
              
              {/* Premium/Free Badge - Only show if course is available */}
              {isAvailable && isPremium && (
                <div className="premium-badge crown">
                  {/* Content added via CSS */}
                </div>
              )}
              
              {isAvailable && !isPremium && (
                <div className="free-badge">
                  FREE
                </div>
              )}
              
              <img src={`${baseUrl}${item.image}`} alt={item.course_title} className="img-fluid" />
              
              <h3 className="card-title mt-2">{item.course_title}</h3>
              <p className="card-text">{shortenString}</p>
              
              {/* Conditional Button */}
              {isAvailable ? (
                <Link to={`/course-description/${item.id}`}>
                  <button className="btn btn-primary text-light" type="button">
                    Start Learning
                  </button>
                </Link>
              ) : (
                <button className="btn btn-secondary" disabled type="button">
                  Coming Soon
                </button>
              )}
            </div>
          </div>
        )
      })
    } catch(e) {
      return(
        <div className="col-12">
          <p className="text-danger">An error occurred. That's all we know.</p>
        </div>
      )
    }
  }

  // Count courses by category for filter badges
  const getCourseCounts = () => {
    const available = data.filter(course => course.available === "true" || course.available === true).length;
    const premium = data.filter(course => 
      (course.available === "true" || course.available === true) && 
      (course.subscription_required === "true" || course.subscription_required === true)
    ).length;
    const free = data.filter(course => 
      (course.available === "true" || course.available === true) && 
      !(course.subscription_required === "true" || course.subscription_required === true)
    ).length;

    return { total: data.length, available, premium, free };
  }

  const courseCounts = getCourseCounts();

  return (
    <MyProSidebarProvider>
      {localStorage.getItem("codefussion-login-token") === null ? window.location.replace("/login") :
      <div className="row">
        <div>
          <TopBar />
        </div>
        <div className="col-md-12">
          {loading ? (
            <div style={{marginLeft: 210, position: 'relative', top: -144 }}>
              <div className="loader-wrapper" id="loading">
                <div className="loader">
                  <div className="loader loader-inner"></div>
                </div>
              </div>
            </div>
          ) : (
            <div className="catalog-container">
              <h2 className="text-dark mt-4 mb-2 font-weight-bold">Explore Courses</h2>
              
              {/* Search Bar */}
              <div className="search-container mb-4">
                <div className="search-box">
                  <i className="fas fa-search search-icon"></i>
                  <input
                    type="text"
                    placeholder="Search courses..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="search-input"
                  />
                  {searchTerm && (
                    <button 
                      className="clear-search"
                      onClick={() => setSearchTerm('')}
                    >
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>
              </div>
              
              {/* Filter Tabs with Counts */}
              <div className="filter-section mb-4">
                <div className="filter-tabs">
                  <button 
                    className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
                    onClick={() => setFilter('all')}
                  >
                    All Courses
                    <span className="count-badge">{courseCounts.total}</span>
                  </button>
                  <button 
                    className={`filter-btn ${filter === 'available' ? 'active' : ''}`}
                    onClick={() => setFilter('available')}
                  >
                    Available Now
                    <span className="count-badge">{courseCounts.available}</span>
                  </button>
                  <button 
                    className={`filter-btn ${filter === 'premium' ? 'active' : ''}`}
                    onClick={() => setFilter('premium')}
                  >
                    Premium
                    <span className="count-badge premium-count">{courseCounts.premium}</span>
                  </button>
                  <button 
                    className={`filter-btn ${filter === 'free' ? 'active' : ''}`}
                    onClick={() => setFilter('free')}
                  >
                    Free
                    <span className="count-badge free-count">{courseCounts.free}</span>
                  </button>
                </div>

                {/* Active Filters Display */}
                {(searchTerm || filter !== 'all') && (
                  <div className="active-filters">
                    <span className="active-filters-label">Active filters:</span>
                    {searchTerm && (
                      <span className="filter-chip">
                        Search: "{searchTerm}"
                        <button onClick={() => setSearchTerm('')}>×</button>
                      </span>
                    )}
                    {filter !== 'all' && (
                      <span className="filter-chip">
                        {filter === 'available' && 'Available Now'}
                        {filter === 'premium' && 'Premium'}
                        {filter === 'free' && 'Free'}
                        <button onClick={() => setFilter('all')}>×</button>
                      </span>
                    )}
                    <button 
                      className="clear-all-filters"
                      onClick={() => {
                        setSearchTerm('');
                        setFilter('all');
                      }}
                    >
                      Clear All
                    </button>
                  </div>
                )}
              </div>

              {/* Results Count */}
              <div className="results-count mb-3">
                <strong>{filteredCourses.length}</strong> 
                {filteredCourses.length === 1 ? ' course' : ' courses'} 
                {searchTerm && ` matching "${searchTerm}"`}
                {filter !== 'all' && ` in ${filter}`}
              </div>
              
              <div className="col-md-12">
                <div className="row row-cols-1 row-cols-md-2 p-3">
                  {showCourses()}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      }

      <style jsx>{`
        .catalog-container {
          padding: 0 1rem;
        }

        /* Search Bar Styles */
        .search-container {
          max-width: 500px;
        }

        .search-box {
          position: relative;
          display: flex;
          align-items: center;
        }

        .search-icon {
          position: absolute;
          left: 15px;
          color: #6c757d;
          z-index: 2;
        }

        .search-input {
          width: 100%;
          padding: 12px 45px 12px 45px;
          border: 2px solid #e0e0e0;
          border-radius: 25px;
          font-size: 1rem;
          transition: all 0.3s ease;
          background: white;
        }

        .search-input:focus {
          outline: none;
          border-color: #0066ff;
          box-shadow: 0 0 0 3px rgba(0, 102, 255, 0.1);
        }

        .clear-search {
          position: absolute;
          right: 15px;
          background: none;
          border: none;
          color: #6c757d;
          cursor: pointer;
          padding: 5px;
          border-radius: 50%;
          transition: all 0.2s ease;
        }

        .clear-search:hover {
          background: #f8f9fa;
          color: #495057;
        }

        /* Filter Section */
        .filter-section {
          border-bottom: 1px solid #e0e0e0;
          padding-bottom: 1rem;
        }

        .filter-tabs {
          display: flex;
          gap: 10px;
          flex-wrap: wrap;
          margin-bottom: 1rem;
        }

        .filter-btn {
          position: relative;
          padding: 10px 20px;
          border: 2px solid #e0e0e0;
          background: white;
          color: #495057;
          border-radius: 25px;
          font-weight: 600;
          cursor: pointer;
          transition: all 0.3s ease;
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .filter-btn:hover {
          border-color: #0066ff;
          color: #0066ff;
          transform: translateY(-2px);
        }

        .filter-btn.active {
          background: #0066ff;
          border-color: #0066ff;
          color: white;
        }

        .count-badge {
          background: rgba(0, 0, 0, 0.1);
          color: inherit;
          padding: 2px 8px;
          border-radius: 12px;
          font-size: 0.8rem;
          font-weight: 600;
        }

        .filter-btn.active .count-badge {
          background: rgba(255, 255, 255, 0.3);
        }

        .premium-count {
          background: rgba(255, 215, 0, 0.2);
          color: #b8860b;
        }

        .free-count {
          background: rgba(0, 204, 136, 0.2);
          color: #008055;
        }

        /* Active Filters */
        .active-filters {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
        }

        .active-filters-label {
          font-weight: 600;
          color: #495057;
        }

        .filter-chip {
          display: flex;
          align-items: center;
          gap: 5px;
          background: #e9ecef;
          padding: 5px 12px;
          border-radius: 20px;
          font-size: 0.9rem;
        }

        .filter-chip button {
          background: none;
          border: none;
          cursor: pointer;
          padding: 2px;
          border-radius: 50%;
          width: 20px;
          height: 20px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.2s ease;
        }

        .filter-chip button:hover {
          background: #dee2e6;
        }

        .clear-all-filters {
          background: none;
          border: none;
          color: #0066ff;
          cursor: pointer;
          font-weight: 600;
          padding: 5px 10px;
          border-radius: 4px;
          transition: background 0.2s ease;
        }

        .clear-all-filters:hover {
          background: #f8f9fa;
        }

        /* Results Count */
        .results-count {
          color: #6c757d;
          font-size: 0.9rem;
        }

        .results-count strong {
          color: #0066ff;
        }

        /* No Courses State */
        .no-courses {
          padding: 3rem 1rem;
        }

        /* Course Card Styles (keep existing) */
        .course-card {
          position: relative;
          overflow: hidden;
          transition: all 0.3s ease;
          border: 1px solid #e0e0e0;
        }

        .course-unavailable {
          opacity: 0.7;
          background-color: #f8f9fa;
        }

        .course-unavailable:hover {
          transform: none;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        }

        /* Badge Styles (keep existing) */
        .unavailable-badge {
          position: absolute;
          top: 12px;
          right: 12px;
          background: linear-gradient(135deg, #6c757d, #495057);
          color: white;
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 1px;
          box-shadow: 0 4px 20px rgba(108, 117, 125, 0.4);
          border: 2px solid #FFF;
          z-index: 2;
          animation: pulse 2s ease-in-out infinite;
        }

        .premium-badge {
          position: absolute;
          top: 12px;
          right: 12px;
          background: linear-gradient(135deg, #FFD700, #FFA500);
          color: #000;
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 1px;
          box-shadow: 0 4px 20px rgba(255, 215, 0, 0.4);
          border: 2px solid #FFF;
          z-index: 2;
          animation: float 3s ease-in-out infinite;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .premium-badge.crown::before {
          content: "👑";
          font-size: 0.8rem;
        }

        .premium-badge.crown::after {
          content: "PREMIUM";
        }

        .free-badge {
          position: absolute;
          top: 12px;
          right: 12px;
          background: linear-gradient(135deg, #00cc88, #00b377);
          color: white;
          padding: 8px 16px;
          border-radius: 20px;
          font-size: 0.75rem;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 1px;
          box-shadow: 0 4px 20px rgba(0, 204, 136, 0.4);
          border: 2px solid #FFF;
          z-index: 2;
          animation: float 3s ease-in-out infinite;
        }

        @keyframes float {
          0%, 100% { 
            transform: translateY(0) rotate(0deg); 
          }
          50% { 
            transform: translateY(-5px) rotate(2deg); 
          }
        }

        @keyframes pulse {
          0%, 100% { 
            transform: scale(1);
            box-shadow: 0 4px 20px rgba(108, 117, 125, 0.4);
          }
          50% { 
            transform: scale(1.05);
            box-shadow: 0 4px 25px rgba(108, 117, 125, 0.6);
          }
        }

        .course-card:hover .premium-badge,
        .course-card:hover .free-badge {
          animation: float 1s ease-in-out infinite;
        }

        .course-card:hover:not(.course-unavailable) {
          transform: translateY(-8px);
          box-shadow: 0 8px 30px rgba(0,0,0,0.12);
          border-color: #0066ff;
        }

        .img-fluid {
          height: 200px;
          object-fit: cover;
          width: 100%;
          border-radius: 8px 8px 0 0;
        }

        .btn-primary {
          background: #0066ff;
          border: none;
          padding: 10px 20px;
          border-radius: 6px;
          font-weight: 600;
          transition: all 0.3s ease;
        }

        .btn-primary:hover {
          background: #0052cc;
          transform: translateY(-2px);
        }

        .btn-secondary {
          background: #6c757d;
          border: none;
          padding: 10px 20px;
          border-radius: 6px;
          font-weight: 600;
          cursor: not-allowed;
          opacity: 0.7;
        }

        .course-unavailable .card-title,
        .course-unavailable .card-text {
          color: #6c757d;
        }

        /* Responsive Design */
        @media (max-width: 768px) {
          .filter-tabs {
            gap: 5px;
          }
          
          .filter-btn {
            padding: 8px 16px;
            font-size: 0.9rem;
          }
          
          .search-input {
            padding: 10px 45px 10px 45px;
          }
        }
      `}</style>
    </MyProSidebarProvider>
  );
}

export default CourseCatalog;